import { studentsControllerGetStudentScheduleSummary } from "@oneapp/data";

const TERMINAL_EXAM_STATUSES = new Set([
  "in_progress",
  "started",
  "attempted",
  "markscalculated",
  "missed",
  "submitted",
]);

function isTerminalExamStatus(examStatus?: string): boolean {
  return TERMINAL_EXAM_STATUSES.has((examStatus ?? "").trim().toLowerCase());
}

export { isTerminalExamStatus };

type ExamLike = Record<string, unknown>;

type SlotLike = {
  start_time?: string;
  end_time?: string;
  assessment?: {
    exams?: ExamLike[];
    assessment_id?: string;
    id?: string;
  };
};

export type GeniusDeepLinkExamContext = {
  slot?: SlotLike;
  exam?: ExamLike;
  examStatus: string;
  startTime?: string;
  endTime?: string;
  resolvedExamId: string;
  questionPaperId: string;
  examMode?: string;
  durationMinutes?: number;
  title: string;
  instructions: string;
  answerSheetUploadDurationMinutes?: number;
  detailReportAvailableAfter?: string;
  studentStartTime?: string;
  studentEndTime?: string;
  assessmentId?: string;
};

function readString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function unwrapDeepLinkTestPayload(response: unknown): {
  exam_slots?: SlotLike[];
} | null {
  const payload = response as
    | {
        data?: { test?: { exam_slots?: SlotLike[] }; exam_slots?: SlotLike[] };
        test?: { exam_slots?: SlotLike[] };
        exam_slots?: SlotLike[];
      }
    | null
    | undefined;

  return (
    payload?.data?.test ?? payload?.test ?? payload?.data ?? payload ?? null
  );
}

function matchesExamIdentifier(exam: ExamLike, examId: string): boolean {
  const normalizedExamId = examId.trim().toLowerCase();
  const candidates = [exam.exam_id, exam.id, exam.rule_id, exam.ruleId]
    .map((value) => readString(String(value ?? ""))?.toLowerCase())
    .filter(Boolean);

  return candidates.includes(normalizedExamId);
}

function extractStartTime(
  exam?: ExamLike,
  slot?: SlotLike,
): string | undefined {
  return (
    readString(exam?.start_time) ??
    readString(exam?.startTime) ??
    readString(exam?.start_date) ??
    readString(slot?.start_time)
  );
}

function extractEndTime(exam?: ExamLike, slot?: SlotLike): string | undefined {
  return (
    readString(exam?.end_time) ??
    readString(exam?.endTime) ??
    readString(exam?.end_date) ??
    readString(slot?.end_time)
  );
}

export async function fetchUpcomingExamFromScheduleSummary(
  scheduleId: string,
  examId: string,
): Promise<GeniusDeepLinkExamContext | null> {
  try {
    const response = (await studentsControllerGetStudentScheduleSummary(
      {
        scheduleIds: [scheduleId],
        status: "upcoming",
        cursor: "",
      },
      { clientName: "oneApp" },
    )) as unknown as {
      data?: {
        schedules?: Array<{
          exam_slots?: SlotLike[];
        }>;
      };
    };

    const schedules = response?.data?.schedules ?? [];

    for (const schedule of schedules) {
      for (const slot of schedule.exam_slots ?? []) {
        for (const exam of slot.assessment?.exams ?? []) {
          if (!matchesExamIdentifier(exam, examId)) continue;

          const startTime = extractStartTime(exam, slot);
          if (!startTime) continue;

          return {
            slot,
            exam,
            examStatus: readString(exam.status)?.toLowerCase() ?? "coming_up",
            startTime,
            endTime: extractEndTime(exam, slot),
            resolvedExamId:
              readString(exam.exam_id) ??
              readString(exam.id) ??
              readString(exam.rule_id) ??
              examId,
            questionPaperId: readString(exam.question_paper_id) ?? "",
            examMode: readString(exam.exam_mode),
            durationMinutes:
              typeof exam.duration_in_minutes === "number"
                ? exam.duration_in_minutes
                : undefined,
            title: readString(exam.title) ?? "Test",
            instructions: readString(exam.instructions) ?? "",
            answerSheetUploadDurationMinutes:
              typeof exam.answer_sheet_upload_duration_minutes === "number"
                ? exam.answer_sheet_upload_duration_minutes
                : undefined,
            detailReportAvailableAfter: readString(
              exam.detail_report_available_after,
            ),
            studentStartTime: readString(exam.student_start_time),
            studentEndTime: readString(exam.student_end_time),
            assessmentId:
              readString(
                (slot.assessment as { assessment_id?: string } | undefined)
                  ?.assessment_id,
              ) ??
              readString((slot.assessment as { id?: string } | undefined)?.id),
          };
        }
      }
    }
  } catch (error) {
    console.warn(
      "[GeniusDeeplink] Failed to resolve upcoming exam from schedule summary:",
      error,
    );
  }

  return null;
}

export function parseGeniusDeepLinkExamContext(
  response: unknown,
  fallbackExamId: string,
): GeniusDeepLinkExamContext | null {
  const testPayload = unwrapDeepLinkTestPayload(response);
  const slot = testPayload?.exam_slots?.[0];
  const assessment = slot?.assessment;
  const exam =
    assessment?.exams?.find((item) =>
      matchesExamIdentifier(item, fallbackExamId),
    ) ?? assessment?.exams?.[0];

  if (!slot && !exam) return null;

  const resolvedExamId =
    readString(exam?.exam_id) ??
    readString(exam?.id) ??
    readString(exam?.rule_id) ??
    fallbackExamId;

  return {
    slot,
    exam,
    examStatus: readString(exam?.status)?.toLowerCase() ?? "",
    startTime: extractStartTime(exam, slot),
    endTime: extractEndTime(exam, slot),
    resolvedExamId,
    questionPaperId: readString(exam?.question_paper_id) ?? "",
    examMode: readString(exam?.exam_mode),
    durationMinutes:
      typeof exam?.duration_in_minutes === "number"
        ? exam.duration_in_minutes
        : undefined,
    title: readString(exam?.title) ?? "Test",
    instructions: readString(exam?.instructions) ?? "",
    answerSheetUploadDurationMinutes:
      typeof exam?.answer_sheet_upload_duration_minutes === "number"
        ? exam.answer_sheet_upload_duration_minutes
        : undefined,
    detailReportAvailableAfter: readString(exam?.detail_report_available_after),
    studentStartTime: readString(exam?.student_start_time),
    studentEndTime: readString(exam?.student_end_time),
    assessmentId:
      readString(assessment?.assessment_id) ?? readString(assessment?.id),
  };
}
